<template>
  <div class="board-container">
    <canvas
      id="board"
      v-bind:width="gridSize.width * cellSize"
      v-bind:height="gridSize.height * cellSize"
      ref="board"
    />
    <canvas
      id="state"
      v-bind:width="gridSize.width * cellSize"
      v-bind:height="gridSize.height * cellSize"
      ref="state"
    />
    <div id="control-panel" ref="controlPanel">
      <TheMoveSelection
        v-if="playerTurn"
        v-bind:cell-size="cellSize"
        v-bind:button-size="navButtonSize"
        v-bind:field-value="board[ballPosition[0]][ballPosition[1]][0]"
        v-on:move="makeMove($event)"
      />
    </div>
  </div>
</template>

<script>
import TheMoveSelection from "@/components/TheMoveSelection";
import {
  BALL_STATE,
  MOVE_DIRECTIONS,
  MOVES,
  EMPTY_STATE,
  BOARD_DIMENSIONS,
  GOAL_PLACEMENT
} from "@/constants";

export default {
  name: "TheBoard",
  components: { TheMoveSelection },
  props: {
    modelValue: {
      type: Array,
      required: true
    },
    playerTurn: {
      type: Boolean,
      required: true
    }
  },
  emits: ["update:modelValue"],
  data() {
    return {
      board: this.modelValue,
      cellSize: 70,
      goalSize: 2,
      navButtonSize: 27
    };
  },
  computed: {
    boardContext() {
      return this.$refs.board.getContext
        ? this.$refs.board.getContext("2d")
        : null;
    },
    stateContext() {
      return this.$refs.state.getContext
        ? this.$refs.state.getContext("2d")
        : null;
    },
    gridSize() {
      return {
        width: BOARD_DIMENSIONS.width - 1,
        height: BOARD_DIMENSIONS.height - 1
      };
    },
    ballPosition() {
      for (let xCoordinate = 0; xCoordinate < this.board.length; xCoordinate++)
        for (
          let yCoordinate = 0;
          yCoordinate < this.board[0].length;
          yCoordinate++
        )
          if (this.board[xCoordinate][yCoordinate][1] === BALL_STATE)
            return [xCoordinate, yCoordinate];
      return [
        Math.floor(BOARD_DIMENSIONS.width / 2),
        Math.floor(BOARD_DIMENSIONS.height / 2)
      ];
    }
  },
  watch: {
    playerTurn() {
      this.drawGameState(this.stateContext);
    }
  },
  methods: {
    drawGrid(ctx) {
      ctx.strokeStyle = "lightgray";
      for (let row = 0; row < this.gridSize.height; row++) {
        if (row === 0 || row === this.gridSize.height - 1) {
          for (let column = 0; column < this.goalSize; column++) {
            ctx.strokeRect(
              this.cellSize * (GOAL_PLACEMENT.begin + column),
              row * this.cellSize,
              this.cellSize,
              this.cellSize
            );
          }
        } else {
          for (let column = 0; column < this.gridSize.width; column++) {
            ctx.strokeRect(
              column * this.cellSize,
              row * this.cellSize,
              this.cellSize,
              this.cellSize
            );
          }
        }
      }
    },
    drawBorders(ctx) {
      ctx.strokeStyle = "black";
      ctx.beginPath();
      ctx.moveTo(0, this.cellSize);
      ctx.lineTo(GOAL_PLACEMENT.begin * this.cellSize, this.cellSize);
      ctx.lineTo(GOAL_PLACEMENT.begin * this.cellSize, 0); //
      ctx.lineTo(GOAL_PLACEMENT.end * this.cellSize, 0);
      ctx.lineTo(GOAL_PLACEMENT.end * this.cellSize, this.cellSize);
      ctx.lineTo(this.gridSize.width * this.cellSize, this.cellSize);
      ctx.lineTo(
        this.gridSize.width * this.cellSize,
        (this.gridSize.height - 1) * this.cellSize
      );
      ctx.lineTo(
        GOAL_PLACEMENT.end * this.cellSize,
        (this.gridSize.height - 1) * this.cellSize
      );
      ctx.lineTo(
        GOAL_PLACEMENT.end * this.cellSize,
        this.gridSize.height * this.cellSize
      );
      ctx.lineTo(
        GOAL_PLACEMENT.begin * this.cellSize,
        this.gridSize.height * this.cellSize
      );
      ctx.lineTo(
        GOAL_PLACEMENT.begin * this.cellSize,
        (this.gridSize.height - 1) * this.cellSize
      );
      ctx.lineTo(0, (this.gridSize.height - 1) * this.cellSize);
      ctx.closePath();
      ctx.stroke();
    },
    drawGoals(ctx) {
      ctx.fillStyle = "blue";
      ctx.fillRect(
        GOAL_PLACEMENT.begin * this.cellSize,
        0,
        this.goalSize * this.cellSize,
        10
      );

      ctx.fillStyle = "red";
      ctx.fillRect(
        GOAL_PLACEMENT.begin * this.cellSize,
        this.gridSize.height * this.cellSize,
        this.goalSize * this.cellSize,
        -10
      );
    },
    drawBoard(ctx) {
      this.drawGrid(ctx);
      this.drawBorders(ctx);
      this.drawGoals(ctx);
    },
    drawGameState(ctx) {
      ctx.clearRect(
        0,
        0,
        BOARD_DIMENSIONS.width * this.cellSize,
        BOARD_DIMENSIONS.height * this.cellSize
      );
      ctx.strokeStyle = "black";
      ctx.fillStyle = this.playerTurn ? "blue" : "red";
      ctx.beginPath();
      for (
        let xCoordinate = 0;
        xCoordinate < this.board.length;
        xCoordinate++
      ) {
        for (
          let yCoordinate = 0;
          yCoordinate < this.board[0].length;
          yCoordinate++
        ) {
          Object.keys(MOVES).forEach(moveId => {
            const isBoundField =
              [1, this.board[0].length - 2].includes(yCoordinate) &&
              (xCoordinate <= GOAL_PLACEMENT.begin ||
                xCoordinate >= GOAL_PLACEMENT.end);
            if (!isBoundField) {
              if (this.board[xCoordinate][yCoordinate][0] & MOVES[moveId]) {
                const nextPosition = this.getNextPosition(
                  [xCoordinate, yCoordinate],
                  moveId
                ).map(coordinate => coordinate * this.cellSize);
                ctx.moveTo(
                  xCoordinate * this.cellSize,
                  yCoordinate * this.cellSize
                );
                ctx.lineTo(nextPosition[0], nextPosition[1]);
                ctx.stroke();
              }
            }
          });
        }
        ctx.closePath();
      }
      ctx.arc(
        this.ballPosition[0] * this.cellSize,
        this.ballPosition[1] * this.cellSize,
        10,
        0,
        Math.PI * 2,
        true
      );
      ctx.fill();
    },
    getNextPosition(position, moveId) {
      return [
        position[0] + MOVE_DIRECTIONS[moveId][0],
        position[1] + MOVE_DIRECTIONS[moveId][1]
      ];
    },
    makeMove(moveId) {
      this.board[this.ballPosition[0]][this.ballPosition[1]][0] |=
        MOVES[moveId];

      const nextPosition = this.getNextPosition(this.ballPosition, moveId);
      const reverseMoveId = Object.keys(MOVES)[
        (Object.keys(MOVES).indexOf(moveId) + 4) % 8
      ];

      this.board[this.ballPosition[0]][this.ballPosition[1]][1] = EMPTY_STATE;
      this.board[nextPosition[0]][nextPosition[1]][0] |= MOVES[reverseMoveId];
      this.board[nextPosition[0]][nextPosition[1]][1] = BALL_STATE;

      this.drawGameState(this.stateContext);
      this.updateControlPanel();

      this.$emit("update:modelValue", {
        board: this.board,
        ballPosition: this.ballPosition
      });
    },
    updateControlPanel() {
      this.$refs.controlPanel.style.top =
        (this.ballPosition[1] - 1) * this.cellSize -
        this.navButtonSize / 2 +
        "px";
      this.$refs.controlPanel.style.left =
        (this.ballPosition[0] - 1) * this.cellSize -
        this.navButtonSize / 2 +
        "px";
    }
  },
  mounted() {
    this.drawBoard(this.boardContext);
    this.drawGameState(this.stateContext);
    this.updateControlPanel();
  }
};
</script>

<style scoped lang="sass">
.board-container
  position: relative
  width: fit-content
  margin: auto

#state
  position: absolute
  top: 0
  left: 0

#control-panel
  position: absolute
</style>
